/** Component for listing all customers, filtering by phone
 * and adding new customers
 */
import React, { useState, useRef } from 'react';
import styled from '@emotion/styled/macro';
import { Table, Space, Button } from 'antd';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { SearchOutlined } from '@ant-design/icons';
import { GET_CUSTOMERS, GENERATE_CUSTOMER_CODE } from 'services';
import { ListHeader, PrimaryButton, PrimaryTitle, Can } from 'components';
import SearchCusomters from 'lists/tickets/search-customers';
import { ValidateUser } from '../../utilities';
import { useTranslation } from 'react-i18next';

const CashoutsListContainer = styled.div``;

const CustomersList = () => {
  ValidateUser();
  const history = useHistory();
  const searchInput = useRef(null);
  const [page, setPage] = useState(1);
  const [filteredValues, setFilteredValues] = useState({
    customerId: null,
  });
  const [t, i18n] = useTranslation();

  const { data, loading, refetch } = useQuery(GET_CUSTOMERS, {
    variables: {
      page: 1,
      first: 6,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const [
    generateCode,
    { loading: generatingCode },
  ] = useMutation(GENERATE_CUSTOMER_CODE, { awaitRefetchQueries: true });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const columns = [
    {
      title: 'Id',
      dataIndex: 'id',
    },
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <SearchCusomters
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            forwardedRef={searchInput}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, 'customerId')}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => handleReset(clearFilters)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filteredValue: filteredValues.customerId,
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current.focus(), 100);
        }
      },
    },
    {
      title: t('Is Registered'),
      key: 'registered',
      render: (customer) => (customer.registered ? t('true') : t('false')),
    },
    {
      title: t('Guest'),
      key: 'registered',
      render: (customer) => (customer.guest ? t('true') : t('false')),
    },
    {
      title: 'Verification Code',
      dataIndex: ['verification_code', 'code'],
      key: 'verification_code',
    },
    {
      title: 'Actions',
      render: (ticket) => {
        const currentTimestamp = new Date();
        const expiryTimestamp = new Date(
          ticket.verification_code && ticket.verification_code.expiry_time
        );
        return (
          <>
            <Space size="middle">
              <Button
                style={{ height: 18, padding: 0 }}
                type="link"
                disabled={currentTimestamp < expiryTimestamp}
                onClick={() =>
                  generateCode({
                    variables: {
                      id: ticket.id,
                    },
                    refetchQueries: () => [
                      {
                        query: GET_CUSTOMERS,
                        variables: {
                          page,
                          first: 6,
                        },
                      },
                    ],
                  })
                }
              >
                {t('Regenerate Code')}
              </Button>
            </Space>
          </>
        );
      },
    },
  ];
  //Translate Function For columns
  const ti81n = columns.map((ele) => {
    ele.title = t(ele.title);
  });

  const onChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setFilteredValues({
      ...filteredValues,
      customerId: filters.phone,
    });
    refetch({
      page: pagination.current,
      customerId: filters.phone || undefined,
    });
  };

  return (
    <CashoutsListContainer>
      <ListHeader>
        <PrimaryTitle>{t('Customers')}</PrimaryTitle>
        <Can
          perform="CREATE_CUSTOMER"
          yes={
            <PrimaryButton onClick={() => history.push('/new-customer')}>
              {t('Add Customer')}
            </PrimaryButton>
          }
        />
      </ListHeader>
      <Table
        bordered
        dataSource={data?.customers?.data}
        columns={columns}
        scroll={{ x: 400 }}
        loading={loading || generatingCode}
        onChange={onChange}
        rowKey="id"
        pagination={{
          total: data?.customers?.paginatorInfo?.total,
          pageSize: 6,
          showSizeChanger: false,
        }}
      />
    </CashoutsListContainer>
  );
};

export default CustomersList;
